<script setup lang="ts">
/**
 * Inline display building block for the `render_bar_chart` tool: a horizontal
 * bar chart of `{ label, value }` series data. Display-only — the backend
 * settles the tool call automatically, so this block never emits a result.
 * Rendered with plain CSS bars (no charting dependency) so it works identically
 * on the admin and candidate surfaces and in reloaded transcripts.
 */
import { computed } from "vue";

interface SeriesPoint {
  label: string;
  value: number;
}

const props = defineProps<{
  input: Record<string, unknown>;
}>();

const title = computed(() => (typeof props.input.title === "string" ? props.input.title : ""));

const series = computed<SeriesPoint[]>(() =>
  Array.isArray(props.input.series)
    ? (props.input.series as unknown[])
        .filter(
          (p): p is SeriesPoint =>
            !!p &&
            typeof p === "object" &&
            typeof (p as SeriesPoint).label === "string" &&
            typeof (p as SeriesPoint).value === "number" &&
            Number.isFinite((p as SeriesPoint).value),
        )
    : [],
);

// Scale against the largest magnitude so 0–1, 0–100 and arbitrary ranges all render sensibly.
const maxValue = computed(() =>
  series.value.reduce((max, p) => Math.max(max, Math.abs(p.value)), 0),
);

function widthPercent(value: number): number {
  if (maxValue.value <= 0) {
    return 0;
  }
  return Math.max(2, Math.round((Math.abs(value) / maxValue.value) * 100));
}

function formatValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
</script>

<template>
  <div
    v-if="series.length > 0"
    class="not-prose space-y-3 rounded-xl border border-border bg-card p-4"
    data-slot="chat-block-bar-chart"
  >
    <p v-if="title" class="text-sm font-medium text-foreground">{{ title }}</p>

    <div class="space-y-2">
      <div v-for="(point, idx) in series" :key="`${point.label}-${idx}`" class="space-y-0.5">
        <div class="flex items-baseline justify-between gap-3">
          <span class="min-w-0 truncate text-xs font-medium text-foreground">{{ point.label }}</span>
          <span class="shrink-0 text-xs tabular-nums text-muted-foreground">{{ formatValue(point.value) }}</span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            class="h-full rounded-full bg-primary transition-[width]"
            :style="{ width: `${widthPercent(point.value)}%` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
